import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Heart, AlertCircle, CheckCircle2, ArrowRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../shared/Card';
import Button from '../shared/Button';
import * as sharingService from '../../services/assessmentSharingService';
import { clearPlace } from '../../services/sharedSessionPlace';

/**
 * Partner Comparison Results
 * Shows side-by-side comparison when both partners complete Values Assessment
 */
const PartnerComparisonResults = ({ assessment, sessionCode, partnerNumber }) => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const markedComplete = useRef(false);

  // Listen for session updates. The first snapshot delivers the current
  // state, so no separate fetch is needed. (Keeping `comparison` out of the
  // dependencies matters: setting it used to re-run this effect, which
  // re-fetched and re-subscribed in a loop.)
  useEffect(() => {
    const unsubscribe = sharingService.subscribeToSharedAssessment(sessionCode, (result) => {
      if (result.success) {
        setSessionData(result.data);
        const p1 = result.data.partnerOne;
        const p2 = result.data.partnerTwo;

        if (p1?.status === 'completed' && p2?.status === 'completed') {
          setComparison(sharingService.compareScores(assessment, p1.scores, p2.scores));
          // Both finished: close the session's status off (both devices may
          // do this; it's the same write either way).
          if (result.data.status !== 'completed' && !markedComplete.current) {
            markedComplete.current = true;
            sharingService.completeSharedAssessment(sessionCode);
          }
        }
      } else {
        // Expired or gone: forget this device's place in it.
        clearPlace(assessment.id);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [sessionCode, assessment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Session Not Found</h2>
          <p className="text-slate-300 mb-6">This session may have expired or been deleted.</p>
          <Button onClick={() => navigate('/assessment')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Assessments
          </Button>
        </Card>
      </div>
    );
  }

  const p1 = sessionData.partnerOne;
  const p2 = sessionData.partnerTwo;
  const partnerWaiting = (partnerNumber === 1 && p2.status !== 'completed') ||
                         (partnerNumber === 2 && p1.status !== 'completed');

  // Waiting for partner screen
  if (partnerWaiting) {
    const otherPartnerName = partnerNumber === 1 ? p2.name : p1.name;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-white animate-pulse" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              You've Completed the Assessment!
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Waiting for {otherPartnerName || 'your partner'} to finish...
            </p>

            <Card className="p-8">
              <p className="text-slate-300 mb-6">
                Your results have been saved. As soon as {otherPartnerName || 'your partner'} completes
                the assessment, you'll both see your comparison results here.
              </p>
              <div className="flex gap-4 justify-center">
                <Button variant="secondary" onClick={() => navigate('/assessment')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  All Assessments
                </Button>
                <Button onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Both completed - show comparison
  if (!comparison) {
    return null;
  }

  const { alignmentScores, alignmentType, partnerOneScores, partnerTwoScores } = comparison;
  const alignmentData = assessment.alignmentTypes[alignmentType];

  // Calculate average alignment
  const avgAlignment = Math.round(
    Object.values(alignmentScores).reduce((a, b) => a + b, 0) / Object.keys(alignmentScores).length
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Values Alignment
          </h1>
          <p className="text-xl text-slate-300">
            {p1.name} & {p2.name}
          </p>
        </motion.div>

        {/* Overall Alignment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="p-8 bg-gradient-to-br from-rose-500/10 to-pink-500/10 border-rose-500/20">
            <div className="text-center mb-6">
              <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-500 mb-2">
                {avgAlignment}%
              </div>
              <p className="text-sm text-slate-400 mb-4">Overall Alignment</p>
              <h2 className="text-3xl font-bold text-white mb-2">
                {alignmentData.name}
              </h2>
              <p className="text-xl text-rose-200 mb-4">
                {alignmentData.tagline}
              </p>
              <p className="text-slate-300 max-w-2xl mx-auto">
                {alignmentData.description}
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Dimension Comparisons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card className="p-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Dimension-by-Dimension Comparison
            </h3>
            <div className="space-y-6">
              {Object.entries(alignmentScores).map(([dimension, alignment]) => {
                const p1Score = partnerOneScores[dimension] || 0;
                const p2Score = partnerTwoScores[dimension] || 0;
                // Scores are per dimension (e.g. childrenDesire); show each one
                // under the value category it belongs to
                const dimensionData = Object.values(assessment.valueCategories || {})
                  .find((category) => category.dimensions?.includes(dimension));

                if (!dimensionData) return null;

                const dimensionName = dimension
                  .replace(/([A-Z])/g, ' $1')
                  .toLowerCase()
                  .replace(/^./, (c) => c.toUpperCase());

                // Determine alignment colour
                const alignmentColor = alignment >= 75 ? 'green' : alignment >= 50 ? 'amber' : 'red';

                return (
                  <div key={dimension} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {dimensionData.icon && <span className="text-2xl">{dimensionData.icon}</span>}
                        <span className="text-white font-medium">
                          {dimensionName}
                          <span className="block text-sm text-slate-400 font-normal">{dimensionData.name}</span>
                        </span>
                      </div>
                      <div className={`flex items-center gap-2 text-${alignmentColor}-400`}>
                        {alignment >= 75 ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <span className="font-bold">{alignment}% aligned</span>
                      </div>
                    </div>

                    {/* Partner 1 Bar */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-400">{p1.name}</span>
                        <span className="text-slate-400">{p1Score}/100</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3">
                        <div
                          className="h-3 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full transition-all"
                          style={{ width: `${p1Score}%` }}
                        />
                      </div>
                    </div>

                    {/* Partner 2 Bar */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-400">{p2.name}</span>
                        <span className="text-slate-400">{p2Score}/100</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3">
                        <div
                          className="h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                          style={{ width: `${p2Score}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </motion.div>

        {/* Insights */}
        {alignmentData.considerations && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card className="p-8">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-amber-400" />
                What This Means for You
              </h3>
              <ul className="space-y-3">
                {alignmentData.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-300">
                    <span className="text-amber-400 mt-1">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="p-8 bg-gradient-to-br from-teal-500/5 to-blue-500/5 border-teal-500/20">
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="secondary" onClick={() => navigate('/assessment')}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                All Assessments
              </Button>
              <Button onClick={() => navigate('/modules')}>
                Explore Modules
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="secondary" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PartnerComparisonResults;
